'use client';

/**
 * The `principalAndMembers` field's editor — one principal plus any number of
 * other members of the same set, in one popover (VALE-166,
 * `components/attributes/CLAUDE.md` §3).
 *
 * **Assembled from the two editors this package already has, not from a third
 * idiom.** The principal half is the same `Select` the `select` branch of
 * `attribute-field-control.tsx` opens; the member half is
 * `attribute-multi-select-editor.tsx`'s checkable `Command` list. Composing
 * them inside one popover is what `attribute-name-editor.tsx` does with a
 * `Select` and three `Input`s, and it keeps the contract identical: the draft is
 * live, `Escape` reverts to the value the field opened with, and dismissing
 * commits.
 *
 * **Why one row rather than two.** Both halves write one array on the matter
 * (`roles`, which the backend full-syncs), so as two separate rows each control
 * had to carry the other's assignments forward or silently unassign them. One
 * draft holding both makes that unexpressible — see the `principalAndMembers`
 * arm's own doc in `attribute-types.ts`.
 */

import { CheckIcon } from 'lucide-react';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  ATTRIBUTE_COMPOSITE_PART,
  ATTRIBUTE_COMPOSITE_PART_CONTROL,
  ATTRIBUTE_COMPOSITE_PART_LABEL,
  ATTRIBUTE_COMPOSITE_PARTS,
  ATTRIBUTE_EDITOR_BARE,
  ATTRIBUTE_INSET,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_POPOVER_COLLISION_PADDING,
  ATTRIBUTE_POPOVER_PANEL,
  ATTRIBUTE_VALUE_TEXT
} from './attribute-tokens';
import { attributePlaceholder } from './attribute-placeholder';
import type {
  AttributeDraft,
  AttributeOption,
  AttributePrincipalMembers
} from './attribute-types';
import {
  commandListToggleOnSpace,
  useAttributePopoverCommit
} from './use-attribute-popover-commit';

/**
 * What the principal picker offers for "nobody".
 *
 * A sentinel rather than `''` for the reason the whole grid already knows: a
 * Radix `SelectItem` cannot hold an empty value. It never leaves this module —
 * the draft carries `principal: null`, so no spec's `toPatch` has to know it
 * exists, which is the difference from `actingSolicitorField`'s own
 * `NO_SOLICITOR` (that one *is* the draft, because a `select` draft is a
 * string).
 */
const NONE = '__none__';

/**
 * The compact read-out the trigger shows — the principal, with the member count
 * appended.
 *
 * Shared with the spec's own `display` in spirit but deliberately re-derived
 * here from the *draft*: while the popover is open the trigger has to track what
 * has been chosen, not what was committed.
 *
 * **Exported for the row's commit bridge** (VALE-1773) — see
 * `attribute-draft-display.ts`, which renders this same output in the read-out
 * while the host's record prop catches up.
 */
export function formatPrincipalMembersSummary(
  value: AttributePrincipalMembers | null,
  options: AttributeOption[],
  noneLabel: string
): string {
  const principal = value?.principal ?? null;
  const members = value?.members ?? [];
  const label = principal
    ? (options.find((option) => option.value === principal)?.label ?? principal)
    : noneLabel;
  return members.length > 0 ? `${label} +${members.length}` : label;
}

export function AttributePrincipalMembersEditor({
  label,
  value,
  principalLabel,
  membersLabel,
  options,
  noneLabel,
  onDraft,
  onRevert,
  onCommit
}: {
  label: string;
  value: AttributePrincipalMembers | null;
  principalLabel: string;
  membersLabel: string;
  options: AttributeOption[];
  noneLabel: string;
  onDraft: (next: AttributeDraft) => void;
  onRevert: () => void;
  onCommit: (value: AttributeDraft) => void;
}) {
  const principal = value?.principal ?? null;
  const members = value?.members ?? [];

  const setPrincipal = (next: string) => {
    const chosen = next === NONE ? null : next;
    /*
      Choosing someone as principal takes them *out* of the member set in the
      same gesture. The alternative is a matter that holds one person twice —
      which the backend would accept and every read-out would then render as
      `Jane Doe +1` where the `+1` is Jane. The drawer drops the acting
      solicitor from its team-member list for the same reason.
    */
    onDraft({
      principal: chosen,
      members: members.filter((member) => member !== chosen)
    });
  };

  const toggleMember = (optionValue: string) => {
    // A draft update, not a commit — the popover stays open so more than one
    // member can be chosen in one visit, exactly what a `Select`'s
    // commit-on-choose cannot offer a set.
    onDraft({
      principal,
      members: members.includes(optionValue)
        ? members.filter((member) => member !== optionValue)
        : [...members, optionValue]
    });
  };

  /*
    Derived from the *drafted* principal, not the committed one, so the person
    just chosen above disappears from the list below without waiting for a
    round-trip. This is the reason the editor takes one `options` list and
    splits it, rather than the spec supplying two — a spec is built from the
    committed matter and cannot see this draft.
  */
  const memberOptions = options.filter((option) => option.value !== principal);

  /*
    `Enter` commits and closes, `Escape` reverts, dismissing commits: the one
    contract all seven composite popover editors share (VALE-1321). See
    `./use-attribute-popover-commit`, which owns every subtlety in it.
  */
  const popoverCommit = useAttributePopoverCommit({
    draft: value,
    onRevert,
    onCommit,
    // The panel is a `cmdk` list, which claims `Enter` the moment a click
    // gives it focus. See the flag's own doc.
    claimEnterBeforeList: true
  });

  return (
    <Popover {...popoverCommit.popover}>
      <PopoverTrigger asChild>
        {/*
          Left-aligned and cell-width, matching the read-out it replaces — the
          trigger sits in the same slot, so the value must not jump sideways as
          the popover opens. Bare: the cell draws the fill and the ring.
        */}
        <button
          type="button"
          aria-label={label}
          className={cn(
            ATTRIBUTE_VALUE_TEXT,
            ATTRIBUTE_INSET,
            ATTRIBUTE_LEADING,
            'text-foreground min-w-0 flex-1 truncate rounded-md text-left',
            ATTRIBUTE_EDITOR_BARE
          )}
        >
          {principal || members.length > 0
            ? formatPrincipalMembersSummary(value, options, noneLabel)
            : attributePlaceholder(label)}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={ATTRIBUTE_POPOVER_COLLISION_PADDING}
        /*
          The shared composite panel and its parts (VALE-863), bound rather than
          restated: this popover shipped as `w-72 space-y-3 p-3`, one step
          narrower and one step looser than the name and address editors it
          sits beside (VALE-1505).
        */
        className={cn(ATTRIBUTE_POPOVER_PANEL, ATTRIBUTE_COMPOSITE_PARTS)}
        {...popoverCommit.content}
      >
        <div className={ATTRIBUTE_COMPOSITE_PART}>
          <Label
            htmlFor="attribute-principal"
            className={ATTRIBUTE_COMPOSITE_PART_LABEL}
          >
            {principalLabel}
          </Label>
          <Select value={principal ?? NONE} onValueChange={setPrincipal}>
            <SelectTrigger
              id="attribute-principal"
              aria-label={principalLabel}
              className={ATTRIBUTE_COMPOSITE_PART_CONTROL}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE} className={ATTRIBUTE_VALUE_TEXT}>
                {noneLabel}
              </SelectItem>
              {options.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className={ATTRIBUTE_VALUE_TEXT}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className={ATTRIBUTE_COMPOSITE_PART}>
          <Label className={ATTRIBUTE_COMPOSITE_PART_LABEL}>
            {membersLabel}
          </Label>
          {/*
            **The list is named by a wrapper, not by a prop on `Command`.**
            This popover holds two sets of `option`s over the same roster — the
            principal picker's and this list's — so an unnamed list leaves a
            screen reader unable to say which set of people it is in. `cmdk`
            writes its own `aria-label` onto the list element (defaulting to the
            generic "Suggestions") and its `label` prop renders elsewhere, so
            neither route survives; a labelled `group` around it is owned by this
            component and cannot be overwritten from inside.
          */}
          {memberOptions.length > 0 ? (
            <div role="group" aria-label={membersLabel}>
              <Command onKeyDown={commandListToggleOnSpace}>
                <CommandList>
                  <CommandGroup>
                    {memberOptions.map((option) => {
                      const isSelected = members.includes(option.value);
                      return (
                        <CommandItem
                          key={option.value}
                          value={option.value}
                          onSelect={() => toggleMember(option.value)}
                          className={cn(ATTRIBUTE_VALUE_TEXT, 'cursor-pointer')}
                        >
                          <span className="min-w-0 flex-1 truncate">
                            {option.label}
                          </span>
                          <CheckIcon
                            className={cn(
                              'h-3.5 w-3.5 shrink-0',
                              isSelected ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </div>
          ) : (
            /*
              A one-person firm, or a roster whose only other member is already
              the principal. An empty `Command` renders as a bare box that reads
              like a list that failed to load.
            */
            <p
              className={cn(
                ATTRIBUTE_VALUE_TEXT,
                'text-muted-foreground px-1 py-1'
              )}
            >
              Nobody else to add.
            </p>
          )}
        </div>
        {popoverCommit.confirm}
      </PopoverContent>
    </Popover>
  );
}
